import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  Button,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  Alert,
  alpha,
  useTheme,
  Rating,
  Avatar,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  DeleteOutline,
  StarRounded,
  Search,
  FilterList,
  DeleteForever,
  PersonOutline,
  SchoolOutlined,
  CalendarToday,
} from '@mui/icons-material';
import { apiService } from '../services/api.service';
import { firebaseService } from '../services/firebase.service';
import { Feedback as FeedbackType } from '../types';
import Layout from '../components/Layout';

const Feedback: React.FC = () => {
  const theme = useTheme();
  const [feedbacks, setFeedbacks] = useState<FeedbackType[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [ratingFilter, setRatingFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string>('');

  const loadFeedbacks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters: any = { page: page + 1, limit: rowsPerPage, search };
      if (ratingFilter) {
        filters.tutorRating = parseInt(ratingFilter, 10);
        filters.rating = parseInt(ratingFilter, 10);
      }
      const result = await apiService.getFeedbacks(filters);
      setFeedbacks(result.data);
      setTotal(result.pagination.total);
    } catch (error: any) {
      console.error(error);
      setError(error.message || 'Failed to load feedbacks.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, ratingFilter, search]);

  useEffect(() => { loadFeedbacks(); }, [loadFeedbacks]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, feedback: FeedbackType) => {
    setAnchorEl(event.currentTarget);
    setSelectedFeedback(feedback);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleViewDetails = () => { setDetailsOpen(true); handleMenuClose(); };
  const handleDeleteClick = () => { setDeleteDialogOpen(true); handleMenuClose(); };

  const handleDeleteConfirm = async () => {
    if (selectedFeedback) {
      try {
        await apiService.deleteFeedback(selectedFeedback.id);
        setDeleteDialogOpen(false);
        loadFeedbacks();
      } catch (error) { console.error(error); setError('Failed to delete feedback'); }
    }
  };

  return (
    <Layout>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{
          mb: 4,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>User Feedback</Typography>
            <Typography color="text.secondary" variant="body2">Monitor platform quality through student and tutor reviews.</Typography>
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}

        <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: theme.shadows[2] }}>
          <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider', bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.02) }}>
            <TextField
              sx={{ flex: { xs: '1 1 100%', sm: 1 }, minWidth: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.05) } }}
              placeholder="Search feedback..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }}
              size="small"
            />

            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 }, flex: { xs: '1 1 100%', sm: 'auto' } }}>
              <InputLabel>Min Rating</InputLabel>
              <Select
                value={ratingFilter}
                onChange={(e) => { setRatingFilter(e.target.value as string); setPage(0); }}
                label="Min Rating"
                sx={{ borderRadius: 3, bgcolor: theme.palette.mode === 'light' ? 'background.paper' : alpha(theme.palette.common.white, 0.05) }}
                IconComponent={FilterList}
              >
                <MenuItem value="">All Ratings</MenuItem>
                <MenuItem value="5">5 Stars only</MenuItem>
                <MenuItem value="4">4+ Stars</MenuItem>
                <MenuItem value="3">3+ Stars</MenuItem>
                <MenuItem value="2">2+ Stars</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px"><CircularProgress size={32} thickness={5} /></Box>
          ) : feedbacks.length === 0 ? (
            <Box display="flex" justifyContent="center" p={8} textAlign="center">
              <Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>No feedback found</Typography>
                <Typography variant="body2" color="text.disabled">Adjust your filters or search terms to see more results.</Typography>
              </Box>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : alpha(theme.palette.common.white, 0.04) }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Participant</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Recipient (Tutor)</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Rating</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Initial Comment</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Dated</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', color: 'text.secondary' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feedbacks.map((fb) => (
                      <TableRow key={fb.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.875rem' }}>{((fb as any).studentName || 'S').charAt(0)}</Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{(fb as any).studentName || (fb as any).userName || 'Unknown'}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fb.studentId?.substring(0, 8) || 'N/A'}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.light', fontSize: '0.875rem' }}>{((fb as any).tutorName || 'T').charAt(0)}</Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{(fb as any).tutorName || 'Unknown'}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{fb.tutorId?.substring(0, 8) || 'N/A'}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Rating value={fb.tutorRating ?? fb.rating ?? 0} readOnly precision={0.5} size="small"
                              icon={<StarRounded fontSize="inherit" color="warning" />}
                              emptyIcon={<StarRounded fontSize="inherit" sx={{ opacity: 0.3 }} />}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>{fb.tutorRating ?? fb.rating ?? ''}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={fb.feedbackText || fb.comment || 'No comment'}>
                            <Typography variant="body2" sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.8, fontSize: '0.8rem' }}>
                              {fb.feedbackText || fb.comment || 'No comment provided'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {fb.createdAt ? firebaseService.convertTimestamp(fb.createdAt).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={(e) => handleMenuClick(e, fb)} size="small"><MoreVert fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, n) => setPage(n)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </>
          )}
        </Paper>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { borderRadius: 3, minWidth: 180, mt: 1 } }}>
          <MenuItem onClick={handleViewDetails} sx={{ gap: 1.5, py: 1.25 }}>
            <Visibility fontSize="small" color="action" /> <Typography variant="body2" fontWeight={600}>View Full Details</Typography>
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ gap: 1.5, py: 1.25, color: 'error.main' }}>
            <DeleteOutline fontSize="small" /> <Typography variant="body2" fontWeight={600}>Remove Feedback</Typography>
          </MenuItem>
        </Menu>

        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>Review Details</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>Feedback Assessment</Typography>
              </Box>
              <Rating value={selectedFeedback?.tutorRating ?? selectedFeedback?.rating ?? 0} readOnly size="large" />
            </Box>

            <Divider sx={{ mb: 3 }} />

            {selectedFeedback && (
              <Stack spacing={3}>
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: alpha(theme.palette.action.hover, 0.5) }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', display: 'block', mb: 1.5 }}>The Feedback</Typography>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.primary', lineHeight: 1.6 }}>
                    "{selectedFeedback.feedbackText || selectedFeedback.comment || 'No comment provided by the student.'}"
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <PersonOutline sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>Student</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{(selectedFeedback as any).studentName || (selectedFeedback as any).userName}</Typography>
                    <Typography variant="caption" color="text.secondary">ID: {selectedFeedback.studentId || selectedFeedback.userId}</Typography>
                  </Box>
                  <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <SchoolOutlined sx={{ fontSize: 16, color: 'secondary.main' }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase' }}>Tutor</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{(selectedFeedback as any).tutorName}</Typography>
                    <Typography variant="caption" color="text.secondary">ID: {selectedFeedback.tutorId}</Typography>
                  </Box>
                </Box>

                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Submitted On</Typography>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 14 }} />
                      {selectedFeedback.createdAt ? firebaseService.convertTimestamp(selectedFeedback.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                  {selectedFeedback.platformRating && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Platform Satisfaction</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Rating value={selectedFeedback.platformRating} readOnly size="small" />
                        <Typography variant="caption">({selectedFeedback.platformRating})</Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
                {selectedFeedback.bookingId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Reference Booking</Typography>
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>#{selectedFeedback.bookingId}</Typography>
                  </Box>
                )}
              </Stack>
            )}
            <Button onClick={() => setDetailsOpen(false)} variant="contained" fullWidth sx={{ mt: 4, py: 1.5, borderRadius: 3, fontWeight: 700 }}>Close Assessment</Button>
          </Box>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', mx: 'auto', mb: 2 }}>
              <DeleteForever sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>Permanent Removal</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Are you sure you want to delete this feedback record? This action will permanently remove the review from the system and tutor metrics.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button fullWidth onClick={() => setDeleteDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
              <Button fullWidth onClick={handleDeleteConfirm} variant="contained" color="error" sx={{ fontWeight: 700, borderRadius: 2 }}>Delete Forever</Button>
            </Stack>
          </Box>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default Feedback;
